import type { Program } from "@hybrid-iptv/types";
import { usePressAndHold } from "./pressAndHold.js";

interface EpgProgramBlockProps {
  program: Program;
  leftPx: number;
  widthPx: number;
  isLive: boolean;
  onClick: () => void;
  /** Long-press / right-click / context-key: opens the quick-action menu at
   * the given viewport point. Omit to disable the menu for this block. */
  onContextAction?: (point: { x: number; y: number }) => void;
  /** Position of this block in the guide — enables D-pad roving focus. */
  guideRow?: number;
  guideCol?: number;
  /** True when this block is the current D-pad target (roving tabindex + ring). */
  isFocused?: boolean;
}

/** A single program cell in the EPG grid. Width is proportional to duration. */
export function EpgProgramBlock({ program, leftPx, widthPx, isLive, onClick, onContextAction, guideRow, guideCol, isFocused = false }: EpgProgramBlockProps) {
  // Tap / OK acts, hold opens the quick-action menu (pressAndHold.ts).
  const press = usePressAndHold(onClick, onContextAction);
  const startMs = new Date(program.start).getTime();
  const endMs = new Date(program.end).getTime();
  const progress = isLive ? Math.min(100, Math.max(0, Math.round(((Date.now() - startMs) / (endMs - startMs)) * 100))) : 0;
  return (
    <div
      className={`epg-program-block${isFocused ? " epg-program-block--focused" : ""}`}
      style={{
        left: leftPx,
        // Its true length, never more. A 60 px minimum (for tapping on a phone) made
        // every programme under ~12 minutes run under the next one: Cartoon
        // Network's 10-minute shorts drew as stacked, cut-off cards with the live
        // dot over the wrong one (owner, 2026-10-08). The remote steps block by
        // block whatever the width; the panel above shows the focused one in full.
        width: widthPx,
      }}
      data-guide-row={guideRow}
      data-guide-col={guideCol}
      // TV-FIRST: press-and-HOLD OK is the remote's long-press, so OK acts
      // on key UP — see usePressAndHold.
      {...press}
      role="button"
      tabIndex={isFocused ? 0 : -1}
      aria-label={`${program.title}${isLive ? " (now playing)" : ""}`}
    >
      {/* On now: the part already aired is shaded, with a white edge at "now", and
          the time left beside the times (owner chose this, 2026-10-08: the 2 px bar
          in the genre colour, mostly grey, could not be told for a progress bar).
          Brightness, not colour, so it reads for a colour-blind viewer too. */}
      {isLive && <div className="epg-program-elapsed" style={{ width: `${progress}%` }} />}
      <span className="epg-program-time">
        {formatTime(program.start)}–{formatTime(program.end)}
        {isLive && <span className="epg-program-left"> · {timeLeft(endMs - Date.now())}</span>}
      </span>
      <span className="epg-program-title">{program.title}</span>
      {isLive && <span className="epg-program-live-dot" />}
    </div>
  );
}

/** "17 min left", "1 h 20 min left". */
export function timeLeft(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) return `${minutes} min left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h left` : `${hours} h ${rest} min left`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return h === 0 && m === "00" ? `${h12}${ampm}` : m === "00" ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

interface EpgEmptyBlockProps {
  widthPx: number;
  /** OK / click: the same as OK on the channel's name — tune it. */
  onClick: () => void;
  /** Long-press / right-click / context-key: the channel's quick-action menu. */
  onContextAction?: (point: { x: number; y: number }) => void;
  guideRow: number;
  isFocused: boolean;
}

/**
 * The block a row shows when its channel has nothing in the guide's window.
 * Without it the row was blank, so Down from a programme dropped the ring onto
 * the channel's name, and the next Up jumped from there back out to the
 * programme (owner, 2026-09-26: "paths going to the closest button"). It is
 * column 0 of its row (guideFocus counts it), and acts like the channel's name.
 */
export function EpgEmptyBlock({ widthPx, onClick, onContextAction, guideRow, isFocused }: EpgEmptyBlockProps) {
  const press = usePressAndHold(onClick, onContextAction);
  return (
    <div
      className={`epg-program-block epg-program-block--empty${isFocused ? " epg-program-block--focused" : ""}`}
      style={{ left: 0, width: widthPx }}
      data-guide-row={guideRow}
      data-guide-col={0}
      {...press}
      role="button"
      tabIndex={isFocused ? 0 : -1}
      aria-label="No programme information"
    >
      <span className="epg-program-title">No information</span>
    </div>
  );
}
